import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import URLs from "constants/urls";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledCancelButton, StyledButton } from "components/StyledComponents/styledComponents";

export default function BusinessBreakdownDialog({
  open,
  onClose,
  startDate,
  endDate,
  dsaId,
  user,
  setAlert,
  isIncomeByMis = false,
  urlApi,
  selectedDsaId,
}) {
  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const jwt_token = Cookie.get("jwt_token");

  const formatIndianCurrency = (value) => {
    if (value == null) return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Define all possible columns including dsaName
  const allColumnDefs = useMemo(
    () => [
      { headerName: "Business Type", field: "businessType", sortable: true, filter: true },
      {
        headerName: "Application Number",
        field: "applicationNumber",
        sortable: true,
        filter: true,
      },
      { headerName: "Customer Name", field: "customerName", sortable: true, filter: true },
      { headerName: "Banker Name", field: "bankerName", sortable: true, filter: true },
      {
        headerName: "DSA Name",
        field: "dsaName",
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.dsaName || "", // Show empty if not in payload
      },
      {
        headerName: "Disbursed Amount",
        field: "disbursedAmount",
        sortable: true,
        filter: true,
        cellStyle: (params) => ({
          fontWeight: params.data?.businessType === "Total" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "Banker Payout",
        field: "amount",
        sortable: true,
        filter: true,
        cellStyle: (params) => ({
          fontWeight: params.data?.businessType === "Total" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
      {
        headerName: "DMA Payout",
        field: "dmaPayout",
        sortable: true,
        filter: true,
        cellStyle: (params) => ({
          fontWeight: params.data?.businessType === "Total" ? "bold" : "500",
          textAlign: "right",
        }),
        valueFormatter: (params) => formatIndianCurrency(params.value),
        aggFunc: "sum",
        type: "numericColumn",
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = allColumnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
      }));
    setColumnOptions(options);
  }, [allColumnDefs]);

  // Filter data based on search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredRowData(rowData);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = rowData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null) return false;

      // Handle numeric fields differently
      if (typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredRowData(filtered);
  }, [rowData, searchColumn, searchText]);

  // Calculate footer totals for filtered rows
  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return {
        disbursedAmount: 0,
        amount: 0,
        dmaPayout: 0,
      };
    }

    return {
      disbursedAmount: data.reduce((sum, row) => sum + (row.disbursedAmount || 0), 0),
      amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
      dmaPayout: data.reduce((sum, row) => sum + (row.dmaPayout || 0), 0),
    };
  }, []);

  // Update footer when data or filter changes
  useEffect(() => {
    const dataToUse = searchText && searchColumn ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);

    const footerRow = {
      businessType: "Total (Filtered)",
      applicationNumber: "",
      customerName: "",
      bankerName: "",
      dsaName: "",
      disbursedAmount: totals.disbursedAmount,
      amount: totals.amount,
      dmaPayout: totals.dmaPayout,
    };

    setPinnedBottomRowData([footerRow]);
  }, [rowData, filteredRowData, searchText, searchColumn, calculateFooterTotals]);

  // Handle search input changes
  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchColumn(null);
    setSearchText("");
  };

  const handleDownloadExcel = () => {
    const dataToExport = searchText && searchColumn ? filteredRowData : rowData;

    if (!dataToExport || dataToExport.length === 0) return;

    const allData = [...dataToExport, ...pinnedBottomRowData];

    // Re-map to readable column structure
    const exportData = allData.map((row) => ({
      "Business Type": row.businessType || "",
      "Application Number": row.applicationNumber || "",
      "Customer Name": row.customerName || "",
      "Banker Name": row.bankerName || "",
      "DSA Name": row.dsaName || "",
      "Disbursed Amount": row.disbursedAmount || 0,
      "Banker Payout": row.amount || 0,
      "DMA Payout": row.dmaPayout || 0,
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Business Breakdown");

    // Auto column width
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));

    // Write to file
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Business_Breakdown_${startDate}_to_${endDate}.xlsx`
    );
  };

  // Fetch data
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      if (!user) {
        setAlert("Please login again", "error");
      }

      let url = "";
      if (isIncomeByMis) {
        if (selectedDsaId === 0) {
          url = `${urlApi}?fromDate=${startDate}&toDate=${endDate}&assignCompanyId=${selectedDsaId}`;
        } else {
          url = `${urlApi}?fromDate=${startDate}&toDate=${endDate}&companyId=${selectedDsaId}`;
        }
      } else {
        if (user.companyId === 0) {
          url = `${URLs.businessBreakdownUrl}?startDate=${startDate}&endDate=${endDate}`;
        } else {
          url = `${URLs.dsaBusinessBreakdownUrl}?startDate=${startDate}&endDate=${endDate}&dsaId=${user.companyId}`;
        }
      }

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });

        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();

        // Ensure dsaName field exists (empty string if not present)
        const processedData = data.map((row) => ({
          ...row,
          dsaName: row.dsaName || "",
        }));

        setRowData(processedData);
        setFilteredRowData(processedData);
      } catch (error) {
        console.error("Error fetching business breakdown:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, startDate, endDate, dsaId, jwt_token]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Business Breakdown</DialogTitle>

      {/* Search Controls */}
      <DialogContent style={{ padding: "0 24px" }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={handleSearchColumnChange}
              renderInput={(params) => (
                <TextField {...params} label="Search Column" variant="outlined" size="small" />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchTextChange}
              disabled={!searchColumn}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              disabled={!searchColumn && !searchText}
              sx={{ mr: 1, color: "#141212" }}
            >
              Clear Search
            </Button>
            <Typography variant="caption" color="#141212">
              {searchText && searchColumn
                ? `Showing ${filteredRowData.length} of ${rowData.length} rows`
                : `Total ${rowData.length} rows`}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Grid Content */}
      <DialogContent style={{ height: 500, position: "relative", paddingTop: 0 }}>
        {loading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact
              rowData={searchText && searchColumn ? filteredRowData : rowData}
              columnDefs={allColumnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                minWidth: 120,
              }}
              animateRows
              pagination
              paginationPageSize={100}
              enableRangeSelection
              suppressAggFuncInHeader
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return {
                    fontWeight: "bold",
                    backgroundColor: "#f5f6f8",
                  };
                }
                return null;
              }}
              statusBar={{
                statusPanels: [
                  { statusPanel: "agTotalRowCountComponent", align: "left" },
                  { statusPanel: "agAggregationComponent", align: "right" },
                ],
              }}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          sx={{ textTransform: "none" }}
          disabled={rowData.length === 0}
        >
          Download Excel
        </StyledButton>
        <StyledCancelButton onClick={onClose} variant="outlined">
          Close
        </StyledCancelButton>
      </DialogActions>
    </Dialog>
  );
}

BusinessBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dsaId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
  setAlert: PropTypes.func.isRequired,
  isIncomeByMis: PropTypes.bool,
  urlApi: PropTypes.string.isRequired,
  selectedDsaId: PropTypes.number,
};
